import React, { useState, useEffect } from 'react';
import { DateRangePicker, RangeKeyDict } from 'react-date-range';
import { addDays, format, setHours, setMinutes, setSeconds } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './DatePickerStyles.css';
import { useAppContext } from 'providers/AppProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';

interface DateRangeState {
  startDate: Date | null;
  endDate: Date | null;
  key: string;
}

interface DatePickerProps {
  name: string;
  initialStartDate?: Date | string;
  initialEndDate?: Date | string;
  onChange: (name: string, startISO: string, endISO: string) => void;
}

const DatePickerComponent: React.FC<DatePickerProps> = ({ name, initialStartDate, initialEndDate, onChange }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const {
    config: { theme },
  } = useAppContext();

  // Coerce input into valid Date or null
  const parseDate = (d?: Date | string | null): Date | null => {
    if (!d) return null;
    const dt = d instanceof Date ? d : new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const [dateRange, setDateRange] = useState<DateRangeState>({
    startDate: parseDate(initialStartDate),
    endDate: parseDate(initialEndDate),
    key: 'selection',
  });

  const [startTime, setStartTime] = useState<string>('00:00:00');
  const [endTime, setEndTime] = useState<string>('23:59:59');

  useEffect(() => {
    const start = parseDate(initialStartDate);
    const end = parseDate(initialEndDate);

    setDateRange({
      startDate: start,
      endDate: end,
      key: 'selection',
    });

    const pad2 = (n: number) => String(n).padStart(2, '0');
    const toTimeStr = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;

    if (start) setStartTime(toTimeStr(start));
    else setStartTime('00:00:00');

    if (end) setEndTime(toTimeStr(end));
    else setEndTime('23:59:59');
  }, [initialStartDate, initialEndDate]);

  const predefinedRanges = [
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 14 Days', days: 14 },
    { label: 'Last 4 Weeks', days: 28 },
    { label: 'Last 12 Weeks', days: 84 },
  ];

  const handleSelect = (ranges: RangeKeyDict) => {
    const sel = ranges.selection;
    setDateRange({
      startDate: parseDate(sel.startDate as Date | string),
      endDate: parseDate(sel.endDate as Date | string),
      key: 'selection',
    });
  };

  const parseTime = (t: string) => {
    const parts = String(t || '').split(':');
    const hh = Number(parts[0] ?? 0);
    const mm = Number(parts[1] ?? 0);
    const ss = Number(parts[2] ?? 0);
    return {
      hh: isNaN(hh) ? 0 : hh,
      mm: isNaN(mm) ? 0 : mm,
      ss: isNaN(ss) ? 0 : ss,
    };
  };

  const formatISOWithOffset = (d: Date) => {
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const pad3 = (n: number) => String(n).padStart(3, '0');

    const year = d.getFullYear();
    const month = pad2(d.getMonth() + 1);
    const day = pad2(d.getDate());
    const hour = pad2(d.getHours());
    const minute = pad2(d.getMinutes());
    const second = pad2(d.getSeconds());
    const ms = pad3(d.getMilliseconds());

    const offMin = -d.getTimezoneOffset();
    const sign = offMin >= 0 ? '+' : '-';
    const abs = Math.abs(offMin);
    const offH = pad2(Math.floor(abs / 60));
    const offM = pad2(abs % 60);

    return `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}${sign}${offH}:${offM}`;
  };

  const applyRange = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setShowDatePicker(false);
      return;
    }

    const { hh: sh, mm: sm, ss: ss1 } = parseTime(startTime);
    const { hh: eh, mm: em, ss: ss2 } = parseTime(endTime);

    const adjustedStart = setSeconds(setMinutes(setHours(dateRange.startDate, sh), sm), ss1);
    adjustedStart.setMilliseconds(0);

    const adjustedEnd = setSeconds(setMinutes(setHours(dateRange.endDate, eh), em), ss2);
    adjustedEnd.setMilliseconds(999);

    const startISO = formatISOWithOffset(adjustedStart);
    const endISO = formatISOWithOffset(adjustedEnd);

    onChange(name, startISO, endISO);
    setShowDatePicker(false);
  };

  const resetRange = () => {
    setDateRange({ startDate: null, endDate: null, key: 'selection' });
    setStartTime('00:00:00');
    setEndTime('23:59:59');
    onChange(name, '', '');
    setShowDatePicker(false);
  };

  // Check validity for display
  const hasValidRange =
    dateRange.startDate instanceof Date && !isNaN(dateRange.startDate.getTime()) && dateRange.endDate instanceof Date && !isNaN(dateRange.endDate.getTime());

  const themeBtnStyle = {
    borderColor: theme === 'dark' ? 'rgb(55, 62, 83)' : '#cbd0dd',
    background: theme === 'dark' ? 'rgb(20, 24, 36)' : 'white',
    color: theme === 'dark' ? 'rgb(159, 166, 188)' : 'black',
  } as const;

  return (
    <div className="date-picker-container">
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <button className="set-range-btn" onClick={() => setShowDatePicker(true)} style={themeBtnStyle}>
          {dateRange.startDate && dateRange.endDate
            ? `${format(dateRange.startDate, 'dd/MM/yyyy')} ${startTime} - ${format(dateRange.endDate, 'dd/MM/yyyy')} ${endTime}`
            : `Select date`}
        </button>

        {hasValidRange && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Reset date range"
            title="Reset"
            onClick={resetRange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') resetRange();
            }}
            style={{
              ...themeBtnStyle,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 12px',
              height: 36,
              borderStyle: 'solid',
              borderWidth: 1,
              borderRadius: 6,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <FontAwesomeIcon icon={faRotateLeft} />
          </div>
        )}
      </div>

      {showDatePicker && (
        <div className="modal-overlay" onClick={() => setShowDatePicker(false)}>
          <div className="date-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="date-picker-sidebar">
              {predefinedRanges.map(({ label, days }) => (
                <button
                  key={label}
                  className="range-btn"
                  onClick={() =>
                    setDateRange({
                      startDate: addDays(new Date(), -days),
                      endDate: new Date(),
                      key: 'selection',
                    })
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="date-picker-content">
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  marginBottom: 10,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12 }}>From time</label>
                  <input type="time" step={1} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12 }}>To time</label>
                  <input type="time" step={1} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </div>
              </div>

              <DateRangePicker
                ranges={[dateRange as any]}
                onChange={handleSelect}
                months={2}
                direction="horizontal"
                showSelectionPreview
                moveRangeOnFirstSelection
              />

              <div className="button-container">
                <button className="apply-btn" onClick={applyRange}>
                  Set Range
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePickerComponent;
